import { useCallback, useEffect, useState, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { XCloseIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/base-components';
import Portal from '@rsos/base-components/Portal';
import { PORTAL_OBLIQUE_IMAGERY_MODAL_PRIORITY } from '@rsos/capstone/src/constants/portalPriorities';
import { CAPSTONE } from '@rsos/constants/appNames';
import { resetState } from '@rsos/reduxDataStore/src/features/geoSelection/geoSelectionSlice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { ObliqueImageryErrorBanner } from '../ObliqueImageryErrorBanner';
import { getObliqueImageryToken } from '../obliqueImageryApi';
import {
  ModalHeaderContainer,
  CloseButton,
  ModalBody,
  EagleViewMap,
  ModalContainer,
  StyledCustomModal,
} from '../styles/ObliqueImageryModal.styles';
import '../styles/ObliqueImageryModalStyles.scss';

const EV_FEATURE_CLICK = 'featureClick';
const ONE_HOUR_IN_SEC = 3600;
const EXPIRATION_THRESHOLD = 5 * 60;

const ObliqueImageryModal = memo(() => {
  const dispatch = useDispatch();
  const coordinates = useSelector(
    state => state.geoSelection.obliqueImagery.coordinates,
  );
  const org = useSelector(state => selectUserOrgByApp(state.sinatra, CAPSTONE));

  const [obliqueImageryMapRefID, setObliqueImageryMapRefID] = useState(null);
  const [evEmbeddedExplorer, setEvEmbeddedExplorer] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [nextTimerTick, setNextTimerTick] = useState(false);
  /**
   *  @constant {map} - remains mounted in the DOM tree,
   * as removing it may cause EagleView to malfunction.
   */
  const [map, setMap] = useState(null);
  useEffect(() => {
    dispatch(resetState());
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dispatch(resetState());
    setHasError(false);
  }, [dispatch]);

  const onBackdropClick = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const getToken = useCallback(async () => {
    const response = await getObliqueImageryToken({ org });
    const token = response.access_token;
    setAuthToken(token);
    setNextTimerTick(
      (response.expires_in || ONE_HOUR_IN_SEC) - EXPIRATION_THRESHOLD,
    );

    if (map) {
      await map.setAuthToken(token);
    }
    return token;
  }, [org, map]);

  useEffect(() => {
    if (nextTimerTick) {
      // Schedule the next timer tick.
      const timerId = setTimeout(() => {
        getToken();
      }, nextTimerTick * 1000); // Convert seconds to milliseconds

      return () => clearTimeout(timerId);
    }

    // watches the update of token instead of timer
  }, [authToken, nextTimerTick, getToken]);

  const loadMap = useCallback(async () => {
    try {
      const authToken = await getToken();
      /**
       *if the @constant authToken fails or returns falsey (undefined, null, false)
       *the map will still render without the capability of oblique imagery (45 deg view)
       */
      if (!!coordinates && obliqueImageryMapRefID) {
        const map = evEmbeddedExplorer.mount(obliqueImageryMapRefID, {
          authToken,
          view: {
            lonLat: { lat: coordinates[1], lon: coordinates[0] },
            zoom: 19,
          },
          enableDualPaneButton: false,
          measurementPanelEnabled: false,
          searchBarEnabled: false,
        });
        map.off(EV_FEATURE_CLICK);
        setMap(map);
      }
    } catch (err) {
      setHasError(true);
    }
  }, [coordinates, obliqueImageryMapRefID, evEmbeddedExplorer, getToken]);

  useEffect(() => {
    if (obliqueImageryMapRefID && window.evScriptLoaded && coordinates) {
      if (!evEmbeddedExplorer) {
        setEvEmbeddedExplorer(new window.ev.EmbeddedExplorer());
      } else if (!!coordinates && !map) {
        loadMap();
      } else if (!!coordinates && map) {
        map.setView({
          lonLat: { lat: coordinates[1], lon: coordinates[0] },
          zoom: 19,
        });
      }
    }
  }, [obliqueImageryMapRefID, evEmbeddedExplorer, coordinates, loadMap, map]);

  return (
    <Portal
      id="oblique-imagery-modal"
      priority={PORTAL_OBLIQUE_IMAGERY_MODAL_PRIORITY}
    >
      <StyledCustomModal isOpened={!!coordinates} onClick={onBackdropClick}>
        <ObliqueImageryErrorBanner
          isOpened={hasError}
          setHasError={setHasError}
        />
        <ModalContainer noFooter onClick={e => e.stopPropagation()}>
          <ModalHeaderContainer>
            <Box height="100%" justify="center" ml="24px">
              <Text fontWeight="semibold" color="primaryText" m="16px 0">
                EagleView Oblique Imagery
              </Text>
            </Box>
            <CloseButton onClick={handleClose}>
              <XCloseIcon color="primaryText" />
            </CloseButton>
          </ModalHeaderContainer>
          <ModalBody>
            <EagleViewMap
              id="oblique-imagery-map"
              ref={node => setObliqueImageryMapRefID(node?.id)}
              bg="foreground"
            />
          </ModalBody>
        </ModalContainer>
      </StyledCustomModal>
    </Portal>
  );
});

export default ObliqueImageryModal;
